// "I'm on it" (ackSos) and "I'm safe" (resolveSos) for one circle's live map.
// The stream brings the outcome to every client; until it arrives, a
// successful request is remembered here so the button doesn't come back.
import { useCallback, useState } from "react";
import { api } from "../api";
import { sosActionOutcome, sosActionText } from "./sos";

export interface SosActionState {
  busy: boolean;
  /** The request succeeded. */
  done: boolean;
  /** Why it failed, for the user. */
  message: string | null;
}

export interface SosActions {
  /** Per alert id. */
  states: Readonly<Record<string, SosActionState>>;
  ack: (alertId: string, name: string) => void;
  resolve: (alertId: string) => void;
}

const IDLE: SosActionState = { busy: false, done: false, message: null };

export function sosActionState(actions: SosActions, alertId: string): SosActionState {
  return actions.states[alertId] ?? IDLE;
}

export function useSosActions(circleId: string): SosActions {
  const [states, setStates] = useState<Readonly<Record<string, SosActionState>>>({});

  // The buttons are disabled while their request is busy.
  const run = useCallback(async (alertId: string, name: string | null, send: () => Promise<{ response: Response }>) => {
    setStates((prev) => ({ ...prev, [alertId]: { busy: true, done: false, message: null } }));
    let next: SosActionState;
    try {
      const outcome = sosActionOutcome((await send()).response.status);
      next = { busy: false, done: outcome.kind === "done", message: sosActionText(outcome, name) };
    } catch {
      next = { busy: false, done: false, message: "Can't reach the server. Check your connection." };
    }
    setStates((prev) => ({ ...prev, [alertId]: next }));
  }, []);

  const ack = useCallback(
    (alertId: string, name: string) =>
      void run(alertId, name, () =>
        api.POST("/circles/{circleId}/sos/{alertId}/ack", { params: { path: { circleId, alertId } } }),
      ),
    [circleId, run],
  );

  const resolve = useCallback(
    (alertId: string) =>
      void run(alertId, null, () => api.POST("/me/sos/{alertId}/resolve", { params: { path: { alertId } } })),
    [run],
  );

  return { states, ack, resolve };
}
